import { Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CellClickedEvent, CellDoubleClickedEvent, GridOptions, GridReadyEvent } from 'ag-grid';
import { constant } from 'app/app.constants';
import { AgGridSortFilterUtils } from 'app/core/ag-grid-utils/ag-grid-sort-filter-utils';
import { AgButtonsEnum, DepositAction, DepositActions, DepositGridName, OptionImportEnum } from 'app/core/enums/tabs-deposits.enum';
import { FormatCurrencyHelper } from 'app/core/helpers/format-currency-helper';
import { CustomDate } from 'app/core/helpers/format-date-helper';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { ToastrAlertHelper } from 'app/core/helpers/toastr-alert-helper-service';
import { BtnCellRendererI } from 'app/core/interfaces/Btn-cell-renderer.interface';
import { IDeposit, IDepositAction, IPosted } from 'app/core/interfaces/deposit.interface';
import { ConciliationService } from 'app/core/services/conciliation.service';
import { BtnCellRendererComponent } from 'app/pages/A2-utils/grid-components/btn-cell-renderer/btn-cell-renderer.component';
import { ParentModule } from 'app/pages/A2-utils/ParentClass/parent-module';
import { UploadsService } from 'app/pages/A2-utils/uploads.service';
import { T2CardComponent } from 'app/trip2-theme/components/t2-card/t2-card.component';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { getSelectNodeAfterFilter } from 'app/utils/helpers';
import { Subscription } from 'rxjs/Subscription';
import { IPostedEdit } from '../../../../core/interfaces/posted.interface';
import { IApiResponse } from 'app/core/interfaces/invoice-external.interface';
import { InvoicesExternalService } from 'app/core/services/invoices-external.service';

@Component({
  selector: 'app-posted',
  templateUrl: './posted.component.html',
  styleUrls: ['./posted.component.scss']
})
export class PostedComponent extends ParentModule implements OnInit, OnDestroy {
  @ViewChild('modalPosted') modalPosted: T2CardComponent;
  @ViewChild('modalImportPosted') modalImportPosted: T2CardComponent;
  @ViewChild('modalCopyPosted') modalCopyPosted: T2CardComponent;
  @ViewChild('filePosted') filePosted: ElementRef;
  @ViewChild('electronicDetailsModal') private electronicDetailsModal: T2CardComponent;
  gridPostedOptions: GridOptions;
  subscriptions: Subscription[] = [];
  postedSearch: FormGroup;
  postedCopyForm: FormGroup;
  reqSearhc: IDeposit = { depositdate: null };
  totalRows = 0;
  optionImportLockBox = OptionImportEnum.Posted;
  agGridButtonActionList = {
    RemoveFilter: AgButtonsEnum.RemoveFilter,
    RemoveSort: AgButtonsEnum.RemoveSort
  };
  enableClearFilter = true;
  enableClearSort = true;
  public actionsPosted = [];
  totalPostedAmountSelected = 0;
  totalPostedAmount = 0;
  selectedRowIndex = null;
  rowsSelected = [];
  headerSelectorRow = false;
  zeroBalanceCheckBox = false;
  noBlankSpace = constant.NO_BLANK_SPACE;
  postedId = 0;
  filterOnlyPostedWriteOff = true;
  electronicTitle = 'Electronic';
  electronicId = null;

  get payorCtrl(): AbstractControl {
    return this.postedSearch.get('payor');
  }

  get depositMonthPostedCtrl(): AbstractControl {
    return this.postedSearch.get('depositdateposted');
  }

  get checkNumberCtrl(): AbstractControl {
    return this.postedSearch.get('checknumber');
  }

  get filesNamesCtrl(): AbstractControl {
    return this.postedSearch.get('file');
  }

  get uploadfilesCtrl(): AbstractControl {
    return this.postedSearch.get('uploadfiles');
  }

  get showFileInputLockBox(): string {
    return '.xls, .xlsx';
  }

  get copyListCtrl(): FormArray {
    return this.postedCopyForm.get('copyList') as FormArray;
  }

  get validRunButton(): boolean {
    return this.depositMonthPostedCtrl.value ||
          (this.checkNumberCtrl.value && this.checkNumberCtrl.value.trim() !== '');
  }

  constructor(
    private fb: FormBuilder,
    private t2LoadingService: T2LoadingService,
    private sweetAlertHelper: SweetAlertHelper,
    protected objServ: ConciliationService,
    private objToast: ToastrAlertHelper,
    private uploadsService: UploadsService,
    protected inject: Injector,
    private   _invoicesExternalService: InvoicesExternalService,
    private _conciliationService: ConciliationService
  ) {
    super('app-deposits', inject);
    this.createForm();
    this.gridBuilder();


    this.subscriptions.push(
      this.objServ.postedEdit$.subscribe((params: any) => {
        if (params && params.action === 'closePostedEdit') {
          this.modalPosted.close();
        }
        if (params && params.action === 'closePostedEditAndRefresh') {
          this.modalPosted.close();
          this.run();
        }
      }));

  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.getDepositbyCurrentMonth();
  }

  getDepositbyCurrentMonth(): void {
    this.postedSearch.get('depositdateposted')
    .patchValue(CustomDate.formatJsV2(CustomDate.getDate()), { emitEvent: false });
  }

  createForm(): void {
    this.postedSearch = this.fb.group({
      depositdateposted: ['', Validators.required],
      file: '',
      uploadfiles: '',
      payor: null,
      checknumber: null
    });

    this.postedCopyForm = this.fb.group({
      copyList: this.fb.array([])
    });
  }

  gridBuilder(): void {
    this.gridPostedOptions = <GridOptions> {
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      rowSelection: 'multiple',
      suppressRowClickSelection: true,
      frameworkComponents: {
        buttonRenderer: BtnCellRendererComponent
      },
      columnDefs: [
        {
          colId: 'postedId',
          checkboxSelection: true,
          headerCheckboxSelection: true,
          width: 60
        },
        {
          headerName: 'Posted ID',
          field: 'id',
          cellStyle: { 'text-align': 'end' },
          width: 100
        },
        {
          headerName: 'Payor',
          field: 'payor',
          cellStyle: { 'text-align': 'start' },
          width: 310
        },
        {
          headerName: 'Amount Paid',
          field: 'amtpaid',
          cellStyle: { 'text-align': 'end' },
          width: 120,
          cellRenderer: (params) => {
            const decimal = 0;
            return params && params.data.amtpaid
            ? '$' + params.data.amtpaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '$' + decimal.toFixed(2);
          },
          filterParams: AgGridSortFilterUtils.sortFilterColumn
        },
        {
          headerName: 'Posted Date',
          field: 'posteddate',
          width: 110,
          valueGetter: (params) => {
            if (params && params.data.posteddate) {
              return CustomDate.formatJs(params.data.posteddate);
            }
          },
          cellStyle: { 'text-align': 'center' }
        },
        {
          headerName: 'Check Date',
          field: 'checkdate',
          width: 110,
          valueGetter: (params) => {
            if (params && params.data.checkdate) {
              return CustomDate.formatJs(params.data.checkdate);
            }
          },
          cellStyle: { 'text-align': 'center' }
        },
        {
          headerName: 'Check Number',
          field: 'checknumber',
          width: 150
        },
        {
          headerName: 'Deposit Date',
          field: 'depositdate',
          width: 110,
          valueGetter: (params) => {
            if (params && params.data.depositdate) {
              return CustomDate.formatJs(params.data.depositdate);
            }
          },
          cellStyle: { 'text-align': 'center' }
        },
        {
          headerName: 'User',
          field: 'rcsql',
          cellStyle: { 'text-align': 'start' },
          width: 100
        },
        {
          headerName: 'Company',
          field: 'company',
          cellStyle: { 'text-align': 'start' },
          width: 100
        },
        {
          headerName: 'Imported On',
          field: 'importedon',
          width: 210,
          hide: true,
          valueGetter: (params) => {
            if (params && params.data && params.data.importedon) {
              return CustomDate.formatJs(params.data.importedon);
            }
          },
          cellRenderer: (params) => {
            let ico = '';
            let wasImported = null;

            if (params && params.data && params.data.importedon) {
              ico = 'fa icofont-check color-success';
              wasImported = 'Imported';
            }

            return wasImported &&
            `<center>${params.data.importedon}
            <i class="${ico}" aria-hidden="true" style='font-size: 20px' title="${wasImported}"></i></center>`;
          }
        },
        {
          headerName: 'Import Origin',
          field: 'importorigin',
          cellStyle: { 'text-align': 'end' },
          width: 1,
          hide: true
        },
        {
          headerName: 'Posted Status ID',
          field: 'postedstatusid',
          width: 1,
          hide: true
        },
        {
          headerName: 'Deposit ID',
          field: 'depositid',
          cellStyle: { 'text-align': 'end' },
          width: 90
        },
        {
          headerName: 'Electronic ID',
          field: 'electronicid',
          cellStyle: { 'text-align': 'end' },
          width: 90
        },
        {
          headerName: 'Claims #',
          field: 'claimscount',
          cellStyle: { 'text-align': 'end' },
          width: 90,
          hide: true
        },
        {
          headerName: 'Export Code',
          field: 'exportcode',
          cellStyle: { 'text-align': 'end' },
          width: 120
        },
        {
          headerName: 'Batch',
          field: 'batch',
          cellStyle: { 'text-align': 'end' },
          width: 100
        },
        {
          headerName: 'CT',
          field: 'credittypecode',
          width: 50
        },
        {
          headerName: 'Actions',
          field: '',
          colId: 'action',
          cellRenderer: 'buttonRenderer',
          cellStyle: {
            color: 'red',
            'font-size': '15px',
            'text-align': 'center'
          },
          width: 80,
          hide: false,
          cellRendererParams: (params: any) => {
            if (params && params.data.id) {
              const OBJ_RETURN: BtnCellRendererI = {
                onClick: () => this.deleteRow(params),
                classFontAwesome: 'fa-trash',
                bgColor: '#fff',
                color: '#ee0000',
                title: `Delete Posted #${params.data.id}`,
                border: '1px solid #ddd',
              };

              return OBJ_RETURN;
            } else {
              return {
                onClick: null,
                classFontAwesome: '',
                title: ``,
                showBtn: false,
              };
            }
          }
        }
      ],
      onGridReady: (event: GridReadyEvent) => {
        if (event) {
          this.gridPostedOptions.api.setRowData([]);
        }
      },
      onCellClicked: (event: CellClickedEvent) => {
        if (event && event.api) {
          event.api.clearFocusedCell();
        }
        if (
          event &&
          event.data &&
          event.data.claimscount
        ) {
          this._conciliationService.setClaimsCount(event.data.claimscount);
        }
        if (
          event &&
          event.data &&
          event.colDef.headerName === 'Electronic ID' &&
          event.data.electronicid
        ) {
          this.electronicId = event.data.electronicid;
          this.electronicTitle = `Electronic # ${event.data.electronicid}`;
          this.electronicDetailsModal.open();
        }
      },
      onRowClicked: (event) => {
        if (event && event.data) {
          this.selectedRowIndex = event.data.id;
        }
        this.gridPostedOptions.api.redrawRows();
      },
      onCellDoubleClicked: (params: CellDoubleClickedEvent) => {
        if (params && params.data && params.data.id && this.actionsPosted.includes(DepositActions.PostedEdit)) {
          this.modalPosted.open();
          this.postedId = params.data.id;
          this.objServ.setPostedEdit('postedEdit', params.data);

        }
      },
      onFilterChanged: () => {
        this.selectedRowIndex = null;
        this.deselectAll();
        this.modalPosted.close();
        this.updateButtonState();
        this.gridPostedOptions.api.redrawRows();
      },
      onSortChanged: () => {
        this.deselectAll();
        this.modalPosted.close();
        this.updateButtonState();
      },
      onSelectionChanged: () => {
        this.onSelectionChanged();
      },
      rowClassRules: {
        'row-default-deposit-grid': (params) => {
          return params.data.id !== null;
        },
        'row-selected-deposit': (params) => {
          return params && params.data && params.data.id === this.selectedRowIndex;
        }
      },
      getRowNodeId: (data) => data.id
    };
  }

  initSubscriptions(): void {
    this.subscriptions.push(
      this.$actions.subscribe({
        next: (actions) => {
          if (actions && actions.length) {
            this.actionsPosted = actions;
          }
        },
        error: (er) => this.sweetAlertHelper.captureException(er)
      }),
      this.objServ.behaviorSubjDepositObs$.subscribe({
        next: (res) => {
          if (res && res.data) {
            this.evaluateDepositActions(res);
          }
        }
      }),
      this.postedSearch.get('depositdateposted')
      .valueChanges.subscribe({
        next: (res) => {
          if (res) {
            this.objServ.behaviorSubjDeposit.next({
              action: DepositAction.MonthPickerSetterValueChangesPostedResp,
              data: res
            });
            this.clearControl('checknumber');
          }
        },
        error: (er) => this.sweetAlertHelper.captureException(er)
      })
    );
  }

  private evaluateDepositActions(value?: IDepositAction): void {
    if (value == null) {
      return;
    }

    const monthPickerActions = [
      DepositAction.MonthPickerSetterValueChangesDeposit,
      DepositAction.MonthPickerSetterValueChangesLockBoxResp,
      DepositAction.MonthPickerSetterValueChangesConciliationResp,
      DepositAction.MonthPickerSetterValueChangesElectronicResp
    ];

    const {action, data} = value;

    if (monthPickerActions.includes(action)) {
      this.depositMonthPostedCtrl.patchValue(data, {emitEvent: false});
    }

    if (action === DepositAction.SearchPosted && data) {
      this.filterOnlyPostedWriteOff = data.filterOnlyPostedWriteOff;

      const req: IPosted = {
        depositdate: CustomDate.formatJs(this.depositMonthPostedCtrl.value, 'MM-yyyy'),
        includeZeroBalance: this.zeroBalanceCheckBox,
        checknumber: this.checkNumberCtrl.value,
        payor: this.payorCtrl.value
      };

      this.searchPosted(req);

      if (this.gridPostedOptions && this.gridPostedOptions.columnApi) {
        const postedDeleteAction = this.actionsPosted.includes(DepositActions.PostedDelete);
        this.gridPostedOptions.columnApi.setColumnVisible('action', postedDeleteAction);
      }
    }
  }

  searchPosted(req: IPosted): void {
    this.selectedRowIndex = null;

    const filterPostedFn = (el) => this.filterOnlyPostedWriteOff ? el.credittypecode === 'WO' : el.credittypecode !== 'WO';

    if (req.depositdate || req.payor || req.checknumber) {
      const filterModel = this.gridPostedOptions.api.getFilterModel();
      const fortModel = this.gridPostedOptions.api.getSortModel();

      this.t2LoadingService.show();
      this.subscriptions.push(
        this.objServ.getPosted(req)
        .finally(() => this.t2LoadingService.hiden())
        .subscribe({
          next: (resp: IPostedEdit[]) => {
            if (resp && resp.length) {
              this.totalRows = resp.length;
              this.gridPostedOptions.api.setRowData(resp.filter(filterPostedFn));
              this.getTotalPostedGrid(DepositGridName.PostedGrid, resp);
              this.gridPostedOptions.api.setFilterModel(filterModel);
              this.gridPostedOptions.api.setSortModel(fortModel);
            } else {
              this.clearCountAndGrid();
            }
          },
          error: (er) => this.sweetAlertHelper.captureException(er)
        })
      );
    } else {
      this.clearCountAndGrid();
    }
  }

  formatCurrency(value: string): string {
    const formatVal = isNaN(Number(value)) ? 0 : Number(value);
    return FormatCurrencyHelper.formatCurrency(formatVal);
  }

  run(): void {
    if (this.validRunButton) {
      const dateMonthPosted = this.depositMonthPostedCtrl.value;
      const checkNumber = this.checkNumberCtrl.value;
      if (checkNumber) {
        this.payorCtrl.patchValue(null);
      }
      const payorPosted = this.payorCtrl.value;
      const req: IPosted = {
        depositdate: checkNumber ? null : CustomDate.formatJs(dateMonthPosted, 'MM-yyyy'),
        payor: checkNumber ? null : payorPosted,
        checknumber: checkNumber,
        includeZeroBalance: this.zeroBalanceCheckBox
      };
      this.searchPosted(req);
    } else {
      this.zeroBalanceCheckBox = false;
      this.clearCountAndGrid();
      this.objToast.error('Action not allowed', '', { positionClass: 'toast-bottom-right' });
    }
  }

  clearControl(controlName: string, value = null): void {
    this.postedSearch.get(controlName).patchValue(value);
  }

  openModalByName(): void {
    this.modalImportPosted.open();
  }

  selectedFiles(event: any): void {
    if (event && event.target && event.target.files && event.target.files.length) {
      let text = '';
      for (const name of event.target.files) {
        text += name.name + ', ';
      }
      this.postedSearch.patchValue({
        file: text,
        uploadfiles: event.target.files,
      });
    }
  }

  resetImportFile(): void {
    this.filePosted.nativeElement.value = '';
    this.postedSearch.patchValue({ file: '', uploadfiles: ''});
  }

  closeModalLockBox(): void {
    this.resetImportFile();
    this.modalImportPosted.close();
  }

  uploadFile(): void {
    if (this.fileSelected()) {
      this.sweetAlertHelper
      .createCustomAlert({
        title: 'Process File',
        text: 'Are you sure you want to process these deposits files?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, import it!',
        cancelButtonText: 'No, cancel!',
      })
      .then((result) => {
        if (result.value) {
          this.t2LoadingService.show();
          const uploadFiles = this.uploadfilesCtrl.value;
          const params = [{ nameParam: 'dataBodySpreadSheet', data: { extension: 'xls' } }];
          this.subscriptions.push(
            this.uploadsService
            .makeFileRequest(`${constant.URL_API_DEPOSIT_UPLOAD_FILE}?optionImport=${this.optionImportLockBox}`,
              params, uploadFiles, 'file')
            .finally(() => this.t2LoadingService.hiden())
            .subscribe({
              next: (resp: any) => {
                if (resp && (resp.totalinserted || resp.totalduplicates || resp.totaltoinsert)) {
                  this.sweetAlertHelper.createCustomAlert({
                    title: 'Import File',
                    text: `Posted Import Success`,
                    type: 'success',
                    html: `<div>Total Inserted: ${resp.totalinserted ? resp.totalinserted : 0 }</div>
                          <div>Total Duplicates: ${resp.totalduplicates ? resp.totalduplicates : 0}</div>
                          <div>Total: ${resp.totaltoinsert ? resp.totaltoinsert : 0}</div>`
                  }).then((alertResult) => {
                     this.closeModalLockBox();
                    if (alertResult.value) {
                      this.updateStatus();
                    }
                  });
                }
              },
              error: (er) => {
                this.sweetAlertHelper.captureException(er)
                .then(() => this.closeModalLockBox());
              }
            })
          );
        }
      });
    } else {
      this.objToast.error('Action not allowed', '', { positionClass: 'toast-bottom-right' });
      this.closeModalLockBox();
    }
  }

  fileSelected(): boolean {
    return this.filesNamesCtrl.value !== '';
  }

  clearFilterAndSort(nameBtn: AgButtonsEnum): void {
    switch (nameBtn) {
      case AgButtonsEnum.RemoveFilter:
        this.totalPostedAmountSelected = this.totalPostedAmount;
        this.gridPostedOptions.api.setFilterModel(null);
        break;
      case AgButtonsEnum.RemoveSort:
        this.gridPostedOptions.api.setSortModel(null);
        break;
      default:
        break;
    }
    this.updateButtonState();
  }

  updateButtonState(): void {
    const filterApply = !Object.keys(this.gridPostedOptions.api.getFilterModel()).length;
    this.enableClearFilter = filterApply;
    this.enableClearSort = !this.gridPostedOptions.api.getSortModel().length;

    if (!filterApply) {
      this.sumTotalPostedSelected();
    }
  }

  deleteRow(event: any): void {
    if (this.actionsPosted.includes(DepositActions.PostedDelete)) {
      if (event && event.colDef && event.colDef.colId === 'action') {
        if (event.data && event.data.id) {
          this.t2LoadingService.show();
          this.subscriptions.push(
            this.objServ.deletePosted(event.data.id)
            .finally(() => this.t2LoadingService.hiden())
            .subscribe({
              next: (res) => {
                if (res && res.message) {
                  this.gridPostedOptions.api.removeItems([event.node]);
                  this.objToast.success(res.message, 'Success!', { positionClass: 'toast-bottom-right' });
                }
              },
              error: (err) => this.sweetAlertHelper.captureException(err)
            })
          );
        }
      }
    } else {
      this.objToast.error('You do not have permission to perform this action', 'Access Denied!',
        { positionClass: 'toast-bottom-right' });
    }
  }

  sumTotalPostedSelected(): void {
    let total = 0;
    this.gridPostedOptions.api.forEachNodeAfterFilterAndSort((node) => {
      if (node && node.data && node.data.amtpaid) {
        total += node.data.amtpaid;
      }
    });
    this.totalPostedAmountSelected = total;
  }

  getTotalPostedGrid(objTable: string, dataSource: any[]): void {
    switch (objTable) {
      case DepositGridName.PostedGrid:
        if (dataSource && dataSource.length) {
          this.totalPostedAmount = dataSource.reduce((sum, item) => sum + item.amtpaid, 0);
          this.totalPostedAmountSelected = dataSource.reduce((sum, item) => sum + item.amtpaid, 0);
        }
        break;
      default:
        break;
    }
  }

  formatCurrencyText(valueFormated): string {
    const formatVal = isNaN(Number(valueFormated)) ? 0 : Number(valueFormated);
    return FormatCurrencyHelper.formatCurrency(formatVal);
  }

  clearCountAndGrid(): void {
    this.totalPostedAmountSelected = 0;
    this.totalRows = 0;
    if (this.gridPostedOptions && this.gridPostedOptions.api) {
      this.gridPostedOptions.api.setRowData([]);
    }
  }

  updateGridCell(data: any) {
    const rowNode = this.gridPostedOptions.api.getRowNode(data.id);
    rowNode.setData(data);
  }

  closeModal(): void {
    this.modalPosted.close();
  }



  onSelectionChanged(): void {
    this.rowsSelected = getSelectNodeAfterFilter(this.gridPostedOptions);
  }

  openCopyPosted(): void {
    if (this.rowsSelected && this.rowsSelected.length && this.actionsPosted.includes(DepositActions.PostedCopy)) {
      const listBuild = this.buildReq(this.rowsSelected);
      this.setFormArray(listBuild);
      this.modalCopyPosted.open();
    }
  }

  buildReq(arrayList: any[]): any[] {
    const bodyReqList = [];
    arrayList.forEach(row => {
      bodyReqList.push({
        id: row.id,
        amtpaid: row.amtpaid,
        checkdate: row.checkdate,
        checknumber: row.checknumber,
        company: row.company,
        depositdate: row.depositdate,
        payor: row.payor,
        posteddate: row.posteddate,
        rcsql: row.rcsql
      });
    });
    return bodyReqList;
  }

  setFormArray(details: any[]): void {
    this.clearRecords();
    details.forEach(detail => {
      const groupIterate = this.createFormGroup(detail);
      this.copyListCtrl.push(groupIterate);
    });
  }

  clearRecords(): void {
    while (this.copyListCtrl.controls.length !== 0) {
      this.copyListCtrl.removeAt(0);
    }
  }

  createFormGroup(row: any): FormGroup {
    const group = this.fb.group({
      depositidrow: [row.id, Validators.required],
      id: null,
      amtpaid: [row.amtpaid, [Validators.required, Validators.pattern(this.noBlankSpace)]],
      checkdate: [row.checkdate ? CustomDate.formatJs(row.checkdate, 'yyyy-MM-dd') : '', Validators.required],
      checknumber: [row.checknumber, [Validators.required, Validators.pattern(this.noBlankSpace)]],
      company: [row.company, [Validators.required, Validators.pattern(this.noBlankSpace)]],
      depositdate: [row.depositdate ? CustomDate.formatJs(row.depositdate, 'yyyy-MM-dd') : '', Validators.required],
      payor: [row.payor, [Validators.required, Validators.pattern(this.noBlankSpace)]],
      posteddate: [row.posteddate ? CustomDate.formatJs(row.posteddate, 'yyyy-MM-dd') : '', Validators.required],
      rcsql: [row.rcsql, [Validators.required, Validators.pattern(this.noBlankSpace)]],
      importorigin: 'Posted Copy'
    });
    return group;
  }

  closeModalPostedCopy(): void {
    this.modalCopyPosted.close();
    this.clearRecords();
  }

  deleteRowCopyPostedModal(i: number): void {
    this.copyListCtrl.removeAt(i);
  }

  postedCopySave(): void {
    if (this.copyListCtrl.valid && this.copyListCtrl.length) {
      this.t2LoadingService.show();
      const dataSource = this.copyListCtrl.value;
      this.subscriptions.push(
        this.objServ.postedCopy(dataSource)
        .finally(() => this.t2LoadingService.hiden())
        .subscribe({
          next: (resp) => {
            if (resp && resp.message) {
              this.rowsSelected = [];
              let idsTxt = '';
              if (resp.data && resp.data.length) {
                idsTxt =  `(${resp.data.join(', ')})`;
              }

              this.objToast.success(`${resp.message}. ${idsTxt}`, 'Posted Copy', { positionClass: 'toast-bottom-right' });
              this.closeModalPostedCopy();
              this.run();
            }
          },
          error: (er) => {
            this.rowsSelected = [];
            this.sweetAlertHelper.captureException(er);
            this.closeModalPostedCopy();
            this.run();
          }
        })
      );
    }
  }

  clearMonthAndPayor(): void {
    this.clearControl('depositdateposted', '');
    this.clearControl('payor');
  }

  deselectAll(): void {
    if (this.gridPostedOptions && this.gridPostedOptions.api) {
      this.gridPostedOptions.api.deselectAll();
    }
  }

  getIncludeZeroBalance(): void {
    this.run();
  }

  updateStatus() {
    this._loading.show();
    this.subscriptions.push(
      this._invoicesExternalService
        .postUpdateStatus()
        .finally(() => {
          this._loading.hiden();
        })
        .subscribe({
          next: (data: IApiResponse) => {
            if (data && !!data.success) {
              this.sweetAlertHelper.createCustomAlert({
                title: data.message,
                type: 'success',
              });
            } else {
              this.sweetAlertHelper.captureException(data.message);
            }
          },
          error: (error) => {
            this.sweetAlertHelper.captureException(error);
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/billing-general/deposits/posted/posted.component.ts